import { matchRoutes, RouteObject } from 'react-router-dom'
import { AuthenticationContextValues } from './AuthenticationContextValues'

export type RouteAuth = {
  scopes: string[]
  mode: 'all' | 'any'
}

export type RouteAuthHandle = {
  auth?: RouteAuth
}

export function isRouteAuthHandle(handle: unknown): handle is { auth: RouteAuth } {
  if (handle !== null && typeof handle === 'object' && 'auth' in handle) {
    const auth = handle.auth
    if (auth && typeof auth === 'object' && 'scopes' in auth && 'mode' in auth) {
      return Array.isArray(auth.scopes) && (auth.mode === 'all' || auth.mode === 'any')
    }
  }
  return false
}

export type AppRoute = Omit<RouteObject, 'children'> & {
  auth?: RouteAuth
  children?: AppRoute[]
}

export function toRouteObjects(routes: AppRoute[]): RouteObject[] {
  return routes.map(
    ({ auth, children, ...rest }) =>
      ({
        ...rest,
        handle: auth ? { ...rest.handle, auth } : rest.handle,
        children: children ? toRouteObjects(children) : undefined
      }) as RouteObject
  )
}

let registeredRoutes: RouteObject[] = []

export function registerRoutes(routes: RouteObject[]): void {
  registeredRoutes = routes
}

export function getRegisteredRoutes(): RouteObject[] {
  return registeredRoutes
}

/**
 * Checks whether the given user is authorized for an absolute path, based on the `auth` handle metadata
 * of every route matched along that path in the registered route tree.
 */
export function isPathAuthorized(path: string, authContext: AuthenticationContextValues): boolean {
  const matches = matchRoutes(getRegisteredRoutes(), path)
  if (!matches) return true

  return matches.every(match => {
    if (!isRouteAuthHandle(match.route.handle)) return true
    const { scopes, mode } = match.route.handle.auth
    return mode === 'all' ? authContext.hasAllScopes(scopes) : authContext.hasAnyScope(scopes)
  })
}
